import AdminConfirmModal from '../shared/AdminConfirmModal';
import { AdminUser } from '../../../types/admin';
import { Typography, Box } from '@mui/material';

interface DeleteUserModalProps {
  open: boolean;
  user: AdminUser | null;
  onConfirm: (userId: string) => Promise<{ success: boolean; error?: string }>;
  onCancel: () => void;
  isLoading?: boolean;
}

/**
 * DeleteUserModal - High-severity confirmation modal for deleting a user
 *
 * Features:
 * - Typed "DELETE" confirmation required
 * - User information display
 * - List of consequences
 * - Error severity styling
 * - Loading state during delete operation
 *
 * Uses AdminConfirmModal with typed confirmation pattern
 */
export default function DeleteUserModal({
  open,
  user,
  onConfirm,
  onCancel,
  isLoading = false,
}: DeleteUserModalProps) {
  const handleConfirm = async () => {
    if (!user) return;
    await onConfirm(user.id);
  };

  if (!user) return null;

  return (
    <AdminConfirmModal
      open={open}
      title="Delete User Account"
      message={
        <Box>
          <Typography variant="body1" gutterBottom>
            You are about to permanently delete this user account:
          </Typography>
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="body1" fontWeight={500}>
              {user.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {user.email}
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              User ID: {user.id}
            </Typography>
          </Box>
          <Typography variant="body2" color="error" sx={{ mt: 2, fontWeight: 500 }}>
            This action cannot be undone.
          </Typography>
        </Box>
      }
      confirmText="Delete User"
      cancelText="Cancel"
      severity="error"
      requireTypedConfirmation={true}
      confirmationText="DELETE"
      consequences={[
        'All user data will be permanently deleted',
        'All associated homes and their data will be removed',
        'All uploaded images and documents will be deleted',
        'All chat history will be permanently erased',
        'Active subscriptions will be cancelled immediately',
        'This action will be logged in the audit trail',
        'The user cannot be restored after deletion',
      ]}
      onConfirm={handleConfirm}
      onCancel={onCancel}
      isLoading={isLoading}
    />
  );
}
